import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
    MIN_CLIP_DURATION,
    clipAngles,
    computeTotalDuration,
    createItemId,
    normalizeAngles,
    deepClone,
    getLayerIndexByKind,
    getLayerKind,
    migrateGraph,
    packSequentially,
    resealClip,
    sanitizeStreamName,
    sortedClips,
    toSafeNumber,
} from './timelineModel';

/**
 * The one place the timeline changes.
 *
 * Every edit is an action against this reducer, which is what makes undo
 * possible at all: the old editor mutated a cloned graph inside the component
 * that drew it, so there was no seam to record history at. Here each
 * graph-touching action snapshots the previous graph first, and undo is a
 * matter of popping that stack.
 *
 * The playhead lives here too, not in the preview. It used to be owned by the
 * player and pushed *down* into the timeline as a read-only decoration, so the
 * timeline could show a playhead but never move one. Now the timeline sets it
 * and the stage reads it.
 */

const HISTORY_LIMIT = 50;

const withHistory = (state, nextGraph) => ({
    ...state,
    graph: nextGraph,
    past: [...state.past, state.graph].slice(-HISTORY_LIMIT),
    future: [],
});

/** Locate an item and its layer by id, across every layer. */
const findItem = (graph, itemId) => {
    const layers = Array.isArray(graph?.layers) ? graph.layers : [];
    for (let layerIndex = 0; layerIndex < layers.length; layerIndex += 1) {
        const items = Array.isArray(layers[layerIndex]?.items) ? layers[layerIndex].items : [];
        const itemIndex = items.findIndex(item => item?.id === itemId);
        if (itemIndex >= 0) return { layerIndex, itemIndex, item: items[itemIndex] };
    }
    return null;
};

const mapLayerItems = (graph, layerIndex, mapper) => {
    const next = deepClone(graph);
    const layer = next.layers?.[layerIndex];
    if (!layer) return graph;
    layer.items = mapper(Array.isArray(layer.items) ? layer.items : []);
    next.total_duration = computeTotalDuration(next);
    return next;
};

/** A source clip's full length, so a trim cannot run off the end of the file. */
const sourceLimit = (clip, assetsByFileName) => {
    const asset = assetsByFileName?.get?.(String(clip?.asset_filename || ''));
    const duration = toSafeNumber(asset?.Duration ?? asset?.duration);
    // Stills have no intrinsic length and images are held for as long as asked.
    return duration && duration > 0 ? duration : Infinity;
};

function reducer(state, action) {
    switch (action.type) {
        // An externally-supplied graph (a loaded workspace, an AI proposal).
        // History resets: undoing across a proposal boundary would put the user
        // in a state that was never theirs.
        case 'reset':
            return {
                ...state,
                graph: migrateGraph(action.graph),
                past: [],
                future: [],
                selection: null,
            };

        case 'undo': {
            if (state.past.length === 0) return state;
            const previous = state.past[state.past.length - 1];
            return {
                ...state,
                graph: previous,
                past: state.past.slice(0, -1),
                future: [state.graph, ...state.future].slice(0, HISTORY_LIMIT),
            };
        }

        case 'redo': {
            if (state.future.length === 0) return state;
            const [next, ...rest] = state.future;
            return {
                ...state,
                graph: next,
                past: [...state.past, state.graph].slice(-HISTORY_LIMIT),
                future: rest,
            };
        }

        case 'setPlayhead':
            return { ...state, playhead: Math.max(0, action.seconds) };

        case 'setPlaying':
            return { ...state, isPlaying: Boolean(action.isPlaying) };

        case 'select':
            return { ...state, selection: action.selection || null };

        case 'insertClip': {
            const { layerIndex, clip, atIndex } = action;
            const nextGraph = mapLayerItems(state.graph, layerIndex, (items) => {
                const ordered = [...items].sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
                const position = Number.isInteger(atIndex)
                    ? Math.max(0, Math.min(ordered.length, atIndex))
                    : ordered.length;

                // Appending leaves everything already on the track exactly where
                // it is; inserting between two clips has to push the rest along.
                if (position >= ordered.length) {
                    const lastEnd = ordered.reduce((max, item) => Math.max(max, item.end ?? 0), 0);
                    return [...ordered, resealClip({ ...clip, start: lastEnd })];
                }
                ordered.splice(position, 0, clip);
                return packSequentially(ordered);
            });
            return { ...withHistory(state, nextGraph), selection: { kind: 'clip', id: clip.id } };
        }

        // Put a clip at a time rather than an index: the overlay track, where
        // nothing is pushed along to make room.
        case 'placeClip': {
            const { layerIndex, clip, start } = action;
            const placed = resealClip({ ...clip, start: Math.max(0, start) });
            const nextGraph = mapLayerItems(state.graph, layerIndex, items => [...items, placed]);
            return { ...withHistory(state, nextGraph), selection: { kind: 'clip', id: placed.id } };
        }

        case 'deleteItem': {
            const found = findItem(state.graph, action.itemId);
            if (!found) return state;
            const kind = getLayerKind(state.graph.layers[found.layerIndex]);
            const nextGraph = mapLayerItems(state.graph, found.layerIndex, (items) => {
                const remaining = items.filter(item => item.id !== action.itemId);
                // Effects sit at absolute times against the clip they annotate,
                // and insets at the moment they were placed; closing a gap
                // under either would silently retime them.
                return kind === 'effects' || kind === 'overlay' ? remaining : packSequentially(
                    remaining.sort((a, b) => (a.start ?? 0) - (b.start ?? 0))
                );
            });
            return {
                ...withHistory(state, nextGraph),
                selection: state.selection?.id === action.itemId ? null : state.selection,
            };
        }

        // Move a clip along its own track. Track position only — the in/out
        // points are untouched, so moving never re-cuts the media.
        case 'moveClip': {
            const found = findItem(state.graph, action.itemId);
            if (!found) return state;
            const nextGraph = mapLayerItems(state.graph, found.layerIndex, items => items.map(item => (
                item.id === action.itemId
                    ? resealClip({ ...item, start: Math.max(0, action.start) })
                    : item
            )));
            return withHistory(state, nextGraph);
        }

        // Drag a trim handle. The edge being dragged moves the in- or out-point
        // *within the source*; the other edge and the source itself stay put.
        // This is the edit the old model could not express.
        case 'trimClip': {
            const found = findItem(state.graph, action.itemId);
            if (!found) return state;
            const limit = sourceLimit(found.item, action.assetsByFileName);

            const nextGraph = mapLayerItems(state.graph, found.layerIndex, items => items.map((item) => {
                if (item.id !== action.itemId) return item;

                if (action.edge === 'left') {
                    // Pulling the left handle right skips into the source and
                    // moves the clip's track position by the same amount, so
                    // the frames under the handle stay put on screen.
                    const maxIn = item.sourceOut - MIN_CLIP_DURATION;
                    const nextIn = Math.max(0, Math.min(maxIn, item.sourceIn + action.deltaSeconds));
                    const consumed = nextIn - item.sourceIn;
                    return resealClip({
                        ...item,
                        sourceIn: nextIn,
                        start: Math.max(0, item.start + consumed),
                        trimmed: true,
                    });
                }

                const minOut = item.sourceIn + MIN_CLIP_DURATION;
                const nextOut = Math.max(minOut, Math.min(limit, item.sourceOut + action.deltaSeconds));
                // A right-edge trim leaves sourceIn at 0, so the flag is the
                // only thing telling the emitter this clip is short of its
                // source. Dropping it would render the whole file.
                return resealClip({ ...item, sourceOut: nextOut, trimmed: true });
            }));
            return withHistory(state, nextGraph);
        }

        // Cut a clip in two at the playhead. Both halves keep the same source;
        // the source position of the cut is what divides their in/out points.
        case 'splitAt': {
            const found = findItem(state.graph, action.itemId);
            if (!found) return state;
            const clip = found.item;
            const offset = action.seconds - clip.start;
            if (offset <= MIN_CLIP_DURATION || offset >= clip.duration - MIN_CLIP_DURATION) return state;

            const cutPoint = clip.sourceIn + offset;
            // Both halves are short of their source by definition, so both
            // carry the flag; the right half would also be caught by its
            // non-zero sourceIn, but the left half has only this.
            const left = resealClip({ ...clip, sourceOut: cutPoint, trimmed: true });
            const right = resealClip({
                ...clip,
                id: createItemId('clip'),
                stream: sanitizeStreamName(`${clip.stream || 'clip'}_b_${Date.now()}`),
                sourceIn: cutPoint,
                start: clip.start + offset,
                trimmed: true,
            });

            const nextGraph = mapLayerItems(state.graph, found.layerIndex, items => (
                items.flatMap(item => (item.id === action.itemId ? [left, right] : [item]))
            ));
            return { ...withHistory(state, nextGraph), selection: { kind: 'clip', id: right.id } };
        }

        case 'addEffect': {
            const layerIndex = getLayerIndexByKind(state.graph, 'effects', 'layer 0');
            if (layerIndex < 0) return state;
            const nextGraph = mapLayerItems(state.graph, layerIndex, items => [...items, action.effect]);
            return { ...withHistory(state, nextGraph), selection: { kind: 'effect', id: action.effect.id } };
        }

        // Patch any item on any layer: an effect's parameters, or a clip's own
        // properties (its volume). One case rather than two because the edit is
        // identical — find it by id, merge the patch — and because coalescing a
        // slider drag into a single undo entry is wanted for both.
        case 'updateItem': {
            const found = findItem(state.graph, action.itemId);
            if (!found) return state;
            const nextGraph = mapLayerItems(state.graph, found.layerIndex, items => items.map(item => (
                item.id === action.itemId ? { ...item, ...action.patch } : item
            )));
            // Dragging a handle in the inspector fires continuously; coalescing
            // keeps one drag from filling the undo stack with 60 entries.
            return action.coalesce
                ? { ...state, graph: nextGraph, future: [] }
                : withHistory(state, nextGraph);
        }

        /* ---- multicam -----------------------------------------------------
           Which camera a segment shows is per segment -- that is the whole
           point of cutting between them. Everything else about the group (the
           list of angles, their offsets, which one carries the sound) is a
           property of the take, so it is written to every segment of the group
           at once. Splitting copies the item, so the halves already share a
           multicamId and stay in step. */

        case 'setAngle': {
            const found = findItem(state.graph, action.itemId);
            if (!found) return state;
            const angles = clipAngles(found.item);
            if (angles.length === 0) return state;
            const index = Math.max(0, Math.min(angles.length - 1, Math.round(action.angleIndex)));

            const nextGraph = mapLayerItems(state.graph, found.layerIndex, items => items.map(item => (
                item.id === action.itemId
                    // asset_filename follows the live angle so that everything
                    // resolving a clip to a file keeps working unchanged.
                    ? { ...item, angleIndex: index, asset_filename: angles[index].asset_filename }
                    : item
            )));
            return withHistory(state, nextGraph);
        }

        case 'updateMulticam': {
            const found = findItem(state.graph, action.itemId);
            if (!found) return state;
            const groupId = found.item?.multicamId;
            if (!groupId) return state;

            const nextGraph = mapLayerItems(state.graph, found.layerIndex, items => items.map((item) => {
                if (item.multicamId !== groupId) return item;
                const merged = { ...item, ...action.patch };
                const angles = clipAngles(merged);
                if (angles.length === 0) return merged;
                // A removed angle can leave an index past the end, and the live
                // filename has to follow whatever the index now points at.
                const index = Math.max(0, Math.min(angles.length - 1, Math.round(merged.angleIndex ?? 0)));
                return {
                    ...merged,
                    angleIndex: index,
                    audioAngleIndex: Math.max(0, Math.min(angles.length - 1, Math.round(merged.audioAngleIndex ?? 0))),
                    asset_filename: angles[index].asset_filename,
                };
            }));
            return withHistory(state, nextGraph);
        }

        // Turn a plain clip into a multicam group, or add another camera to one
        // that already is. The clip's own source becomes angle 1 at offset 0:
        // it is the clock the other angles are measured against.
        case 'addAngle': {
            const found = findItem(state.graph, action.itemId);
            if (!found) return state;
            const newAngle = normalizeAngles([action.angle])[0];
            if (!newAngle) return state;

            const existing = clipAngles(found.item);
            const seeded = existing.length > 0 ? existing : normalizeAngles([{
                asset_filename: found.item.asset_filename,
                offset: 0,
            }]);
            if (seeded.length === 0) return state;
            const angles = [...seeded, newAngle];
            const groupId = found.item.multicamId || createItemId('mc');

            const nextGraph = mapLayerItems(state.graph, found.layerIndex, items => items.map((item) => {
                const inGroup = found.item.multicamId
                    ? item.multicamId === groupId
                    : item.id === action.itemId;
                if (!inGroup) return item;
                return {
                    ...item,
                    multicamId: groupId,
                    angles,
                    angleIndex: Math.max(0, Math.min(angles.length - 1, Math.round(item.angleIndex ?? 0))),
                    audioAngleIndex: Math.max(0, Math.min(angles.length - 1, Math.round(item.audioAngleIndex ?? 0))),
                };
            }));
            return withHistory(state, nextGraph);
        }

        default:
            return state;
    }
}

export function useTimelineStore({ graph, assets = [], onGraphChange }) {
    const [state, dispatch] = useReducer(reducer, undefined, () => ({
        graph: migrateGraph(graph),
        past: [],
        future: [],
        playhead: 0,
        isPlaying: false,
        selection: null,
    }));

    const assetsByFileName = useMemo(() => {
        const map = new Map();
        (Array.isArray(assets) ? assets : []).forEach((asset) => {
            if (asset?.FileName) map.set(String(asset.FileName), asset);
        });
        return map;
    }, [assets]);

    // Distinguish "the parent handed us a new graph" from "we changed it and
    // the parent echoed it back". Without this the two chase each other and
    // every local edit is immediately reset.
    const lastEmittedRef = useRef(state.graph);
    const incomingRef = useRef(graph);

    useEffect(() => {
        if (graph === incomingRef.current) return;
        incomingRef.current = graph;
        if (graph === lastEmittedRef.current) return;
        dispatch({ type: 'reset', graph });
    }, [graph]);

    useEffect(() => {
        if (state.graph === lastEmittedRef.current) return;
        lastEmittedRef.current = state.graph;
        onGraphChange?.(state.graph);
    }, [state.graph, onGraphChange]);

    const totalDuration = useMemo(() => computeTotalDuration(state.graph), [state.graph]);

    const videoLayerIndex = useMemo(() => getLayerIndexByKind(state.graph, 'video', 'layer 1'), [state.graph]);

    /** The clip under a given time on the video track — what the stage draws. */
    const clipAt = useCallback((seconds) => {
        const layer = state.graph.layers?.[videoLayerIndex];
        return sortedClips(layer).find(clip => seconds >= clip.start && seconds < clip.end) || null;
    }, [state.graph, videoLayerIndex]);

    const selectedItem = useMemo(() => (
        state.selection ? findItem(state.graph, state.selection.id)?.item || null : null
    ), [state.graph, state.selection]);

    const actions = useMemo(() => ({
        reset: (nextGraph) => dispatch({ type: 'reset', graph: nextGraph }),
        undo: () => dispatch({ type: 'undo' }),
        redo: () => dispatch({ type: 'redo' }),
        setPlayhead: (seconds) => dispatch({ type: 'setPlayhead', seconds }),
        setPlaying: (isPlaying) => dispatch({ type: 'setPlaying', isPlaying }),
        select: (selection) => dispatch({ type: 'select', selection }),
        insertClip: (layerIndex, clip, atIndex) => dispatch({ type: 'insertClip', layerIndex, clip, atIndex }),
        placeClip: (layerIndex, clip, start) => dispatch({ type: 'placeClip', layerIndex, clip, start }),
        deleteItem: (itemId) => dispatch({ type: 'deleteItem', itemId }),
        moveClip: (itemId, start) => dispatch({ type: 'moveClip', itemId, start }),
        trimClip: (itemId, edge, deltaSeconds) => dispatch({
            type: 'trimClip', itemId, edge, deltaSeconds, assetsByFileName,
        }),
        splitAt: (itemId, seconds) => dispatch({ type: 'splitAt', itemId, seconds }),
        setAngle: (itemId, angleIndex) => dispatch({ type: 'setAngle', itemId, angleIndex }),
        addAngle: (itemId, angle) => dispatch({ type: 'addAngle', itemId, angle }),
        updateMulticam: (itemId, patch) => dispatch({ type: 'updateMulticam', itemId, patch }),
        addEffect: (effect) => dispatch({ type: 'addEffect', effect }),
        updateItem: (itemId, patch, coalesce) => dispatch({ type: 'updateItem', itemId, patch, coalesce }),
        updateEffect: (itemId, patch, coalesce) => dispatch({ type: 'updateItem', itemId, patch, coalesce }),
    }), [assetsByFileName]);

    return {
        graph: state.graph,
        playhead: state.playhead,
        isPlaying: state.isPlaying,
        selection: state.selection,
        selectedItem,
        totalDuration,
        videoLayerIndex,
        canUndo: state.past.length > 0,
        canRedo: state.future.length > 0,
        clipAt,
        assetsByFileName,
        actions,
    };
}
